import {
  FilingCaseType,
  FilingType,
  PndStatus,
  PndType,
  TaxIncomeType,
  TransactionType,
  WhtRateType,
} from "@/enums/enums";
import { i18n } from "@/i18n";
import { ContactModel } from "@/model/contact/contactModel";
import {
  PndDocumentModel,
  PndDocumentTemplateModel,
  PndOnlineViewModel,
  WhtCertificateItemForPaperModel,
} from "@/model/document/pndDocumentModel";
import { SettingPndDocumentTemplateModel } from "@/model/document/settingDocumentTemplate";
import { WhtCertificateItemModel, WhtCertificateModel } from "@/model/document/witholdingTaxDocumentModal";
import { MerchantDetail } from "@/model/merchant/merchantModel";
import { UserInfoModel } from "@/model/user/userModel";
import {
  GetDocumentMerchant,
  GetDocumentTags,
  GetDocumentUser,
  GetWhtCertificateModelList,
} from "./documentFacadeHelper";
import { GetSignatureUrlUserApprove, GetSignatureUrlUserCreate } from "./documentHelper";

export function GetPndDocumentData(data: any): PndDocumentModel {
  const documentDataModel: PndDocumentModel = {
    approvedDateTime: data.approvedDateTime ? data.approvedDateTime : "",
    approvedUser: data.approvedUser ? GetDocumentUser(data.approvedUser) : new UserInfoModel(),
    countWhtCertificate: data.countWhtCertificate ? data.countWhtCertificate : 0,
    createdDateTime: data.createdDateTime ? data.createdDateTime : 0,
    createdUser: data.createdUser ? GetDocumentUser(data.createdUser) : new UserInfoModel(),
    filingCaseType: data.filingCaseType ? data.filingCaseType : FilingCaseType.None,
    filingRound: data.filingRound ? data.filingRound : 0,
    filingType: data.filingType ? data.filingType : FilingType.None,
    isClosingEntry: data.isClosingEntry ? data.isClosingEntry : false,
    isDeleted: data.isDeleted ? data.isDeleted : false,
    isFiled: data.isFiled ? data.isFiled : false,
    isPaid: data.isPaid ? data.isPaid : false,
    merchant: data.merchant ? GetDocumentMerchant(data.merchant) : new MerchantDetail(),
    netAmount: data.netAmount ? data.netAmount : 0,
    pndDate: data.pndDate ? data.pndDate : "",
    pndId: data.pndId ? data.pndId : 0,
    pndNumber: data.pndNumber ? data.pndNumber : "",
    pndType: data.pndType ? data.pndType : PndType.None,
    status: data.status ? data.status : PndStatus.AwaitFiling,
    subTotal: data.subTotal ? data.subTotal : 0,
    surchargeAmount: data.surchargeAmount ? data.surchargeAmount : 0,
    tags: data.tags ? GetDocumentTags(data.tags) : [],
    updatedDateTime: data.updatedDateTime ? data.updatedDateTime : "",
    updatedUser: data.createdUser ? GetDocumentUser(data.createdUser) : new UserInfoModel(),
    whtTotal: data.whtTotal ? data.whtTotal : 0,
    whtCertificateModelList: data.whtCertificateModelList
      ? GetWhtCertificateModelList(data.whtCertificateModelList)
      : new Array<WhtCertificateModel>(),
  };

  return documentDataModel;
}

export function GetPndDocumentTemplate(
  documentModel: PndOnlineViewModel,
  settingDocumentTemplateDefault?: SettingPndDocumentTemplateModel
): PndDocumentTemplateModel {
  let transactionType = TransactionType.None;
  switch (documentModel.document.data.pndType) {
    case PndType.PND1:
      transactionType = TransactionType.Pnd1;
      break;
    case PndType.PND2:
      transactionType = TransactionType.Pnd2;
      break;
    case PndType.PND3:
      transactionType = TransactionType.Pnd3;
      break;
    case PndType.PND53:
      transactionType = TransactionType.Pnd53;
      break;
  }
  const documentTemplate: PndDocumentTemplateModel = {
    transactionType: transactionType,
    pndType: documentModel.document.data.pndType,
    pndNumber: documentModel.document.data.pndNumber,
    taxPeriodDate: new Date(documentModel.document.data.pndDate),
    filingType: documentModel.document.data.filingType,
    times: documentModel.document.data.filingRound,
    subTotal: documentModel.document.data.subTotal,
    whtTotal: documentModel.document.data.whtTotal,
    surchargeAmount: documentModel.document.data.surchargeAmount,
    netAmount: documentModel.document.data.netAmount,
    filingCaseType: documentModel.document.data.filingCaseType,
    countWhtCertificate: documentModel.document.data.countWhtCertificate,
    merchant: documentModel.document.data.merchant,
    userApprove: {
      isShowSignatureUrl: documentModel.template.certified.isShowUserApproveUrl ?? true,
      isShowApproveDate: documentModel.template.certified.isShowUserApproveDate ?? true,
      isShowApproveName: documentModel.template.certified.isShowUserApproveName ?? true,
      approveDate: new Date(documentModel.document.data.approvedDateTime),
      name:
        documentModel.document.data.approvedUser.firstName + " " + documentModel.document.data.approvedUser.lastName,
      signatureUrl: GetSignatureUrlUserApprove(documentModel),
    },
    userCreate: {
      isShowSignatureUrl: documentModel.template.certified.isShowUserCreateUrl ?? true,
      isShowCreateDate: documentModel.template.certified.isShowUserCreateDate ?? true,
      isShowCreateName: documentModel.template.certified.isShowUserCreateName ?? true,
      createDate: new Date(documentModel.document.data.createdDateTime),
      name: documentModel.document.data.createdUser.firstName + " " + documentModel.document.data.createdUser.lastName,
      signatureUrl: GetSignatureUrlUserCreate(documentModel),
    },
    merchantStampUrl: {
      value: documentModel.credential.merchantCredential.stampUrl,
      isShowElement: documentModel.template.certified.isShowMerchantStampUrl,
    },
    whtCertificateModelList: documentModel.document.data.whtCertificateModelList,
    isDeleted: documentModel.document.data.isDeleted,
  };

  if (settingDocumentTemplateDefault) {
    documentTemplate.userApprove.isShowSignatureUrl =
      settingDocumentTemplateDefault.certified.isShowUserApproveUrl ?? true;
    documentTemplate.userApprove.isShowApproveDate =
      settingDocumentTemplateDefault.certified.isShowUserApproveDate ?? true;
    documentTemplate.userCreate.isShowSignatureUrl =
      settingDocumentTemplateDefault.certified.isShowUserCreateUrl ?? true;
    documentTemplate.userCreate.isShowCreateDate =
      settingDocumentTemplateDefault.certified.isShowUserCreateDate ?? true;
    documentTemplate.merchantStampUrl.isShowElement = settingDocumentTemplateDefault.certified.isShowMerchantStampUrl;
  }

  return documentTemplate;
}

export function GetWhtRate(_whtRate: WhtRateType) {
  let whtRate: any = "";

  const optionsWhtRateType = [
    { id: WhtRateType.Custom, name: "", value: 0 },
    { id: WhtRateType.ZeroPointSevenFive, name: "0.75%", value: 0.75 },
    { id: WhtRateType.OnePercent, name: "1%", value: 1 },
    { id: WhtRateType.OnePointFivePercent, name: "1.5%", value: 1.5 },
    { id: WhtRateType.TwoPercent, name: "2%", value: 2 },
    { id: WhtRateType.ThreePercent, name: "3%", value: 3 },
    { id: WhtRateType.FivePercent, name: "5%", value: 5 },
    { id: WhtRateType.TenPercent, name: "10%", value: 10 },
    { id: WhtRateType.FifteenPercent, name: "15%", value: 15 },
  ];

  whtRate = optionsWhtRateType.find((x) => x.value === _whtRate);

  if (whtRate) {
    return whtRate.name;
  } else {
    return "";
  }
}

export function GetTaxIncomeType(taxIncomeType: TaxIncomeType) {
  let taxIncomeTypeName: any = "";

  const taxIncomeTypeList = [
    { id: TaxIncomeType.SalariesIncome, name: i18n.t("IncomeType.SalariesIncome"), code: "40(1)1" },
    {
      id: TaxIncomeType.SalariesIncomeHaveWithholdingTaxRate,
      name: i18n.t("IncomeType.SalariesIncome"),
      code: "40(1)2",
    },
    { id: TaxIncomeType.SeverancePay, name: i18n.t("IncomeType.SeverancePay"), code: "40(1)2" },
    { id: TaxIncomeType.SalariesWorkingIncome, name: i18n.t("IncomeType.SalariesWorkingIncome"), code: "40(2)" },
    { id: TaxIncomeType.GoodwillAndRightIncome, name: i18n.t("IncomeType.GoodWillAndRightIncome"), code: "40(3)" },
    { id: TaxIncomeType.InterestOrDiscountIncome, name: i18n.t("IncomeType.DiscountIncome"), code: "40(4)ก" },
    { id: TaxIncomeType.ProfitSharingIncome, name: i18n.t("IncomeType.ProfitSharingIncome"), code: "40(4)ข" },
    { id: TaxIncomeType.BonusToShareholder, name: i18n.t("IncomeType.BonusToShareHolder"), code: "40(4)ค" },
    { id: TaxIncomeType.PortionOfDecapitalized, name: i18n.t("IncomeType.PortionOfDecapitalized"), code: "40(4)ง" },
    { id: TaxIncomeType.IncreasedCapital, name: i18n.t("IncomeType.IncreasedCapital"), code: "40(4)จ" },
    { id: TaxIncomeType.BenefitFromConsolidated, name: i18n.t("IncomeType.BenefitFromConsolidated"), code: "40(4)ฉ" },
    { id: TaxIncomeType.BenefitFromTrade, name: i18n.t("IncomeType.BenefitFromTrade"), code: "40(4)ช" },
    { id: TaxIncomeType.AssetRentalIncome, name: i18n.t("IncomeType.AssetRentalIncome"), code: "40(5)" },
    {
      id: TaxIncomeType.ProfessionalServiceIncome,
      name: i18n.t("IncomeType.ProfessionalServiceIncome"),
      code: "40(6)",
    },
    { id: TaxIncomeType.ContractWorkIncome, name: i18n.t("IncomeType.ContractWorkIncome"), code: "40(7)" },
    { id: TaxIncomeType.HireOfWorkIncome, name: i18n.t("IncomeType.HireOfWorkIncome"), code: "40(8)1" },
    { id: TaxIncomeType.AdvertisingIncome, name: i18n.t("IncomeType.AdvertisingIncome"), code: "40(8)2" },
    { id: TaxIncomeType.PrizeOrRewardIncome, name: i18n.t("IncomeType.PrizeOrRewardIncome"), code: "40(8)3" },
    {
      id: TaxIncomeType.OtherServicesExcludeHotelRestaurantAndInsurance,
      name: i18n.t("IncomeType.OtherServicesExcludeHotelRestaurantAndInsurance"),
      code: "40(8)4",
    },
    { id: TaxIncomeType.DiscountOrTradePromotion, name: i18n.t("IncomeType.DiscountOrTradePromotion"), code: "40(8)5" },
    { id: TaxIncomeType.Insurance, name: i18n.t("IncomeType.Insurance"), code: "40(8)6" },
    { id: TaxIncomeType.NonPublicTransport, name: i18n.t("IncomeType.NonPublicTransport"), code: "40(8)7" },
    { id: TaxIncomeType.CelebrityHiring, name: i18n.t("IncomeType.CelebrityHiring"), code: "40(8)8" },
  ];

  taxIncomeTypeName = taxIncomeTypeList.find((x) => x.id == taxIncomeType);
  if (taxIncomeTypeName) {
    return (taxIncomeTypeName = taxIncomeTypeName.code + " " + taxIncomeTypeName.name);
  } else {
    return "";
  }
}

export function GetWhtCertificateItem() {
  const whtCertificateItem: WhtCertificateItemModel = {
    conditionType: 0,
    sequenceNumber: 0,
    documentContactModel: new ContactModel(),
    isTaxIncomeOnly: false,
    itemList: [],
    sourceId: 0,
    sourceType: 0,
    subTotal: 0,
    whtCertificateId: 0,
    whtDate: new Date(),
    whtNumber: "",
    whtTotal: 0,
  };

  return whtCertificateItem;
}

export function GetWhtCertificateItemForPaper() {
  const whtCertificateItemForPaper: WhtCertificateItemForPaperModel = {
    whtCertificateOne: GetWhtCertificateItem(),
    whtCertificateTwo: GetWhtCertificateItem(),
    whtCertificateThree: GetWhtCertificateItem(),
    whtCertificateFour: GetWhtCertificateItem(),
    whtCertificateFive: GetWhtCertificateItem(),
    whtCertificateSix: GetWhtCertificateItem(),
  };
  return whtCertificateItemForPaper;
}
